import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import {
  FaTint,
  FaCalendarDay,
  FaCalendarWeek,
  FaCalendarAlt,
  FaSyncAlt,
  FaFilter,
  FaDatabase,
} from "react-icons/fa";
import "../styles/SuperAdminWaterUsage.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://depotaircerdas.com/api";

const formatNumber = (value, digits = 2) => {
  const num = Number(value || 0);
  return new Intl.NumberFormat("id-ID", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(num);
};

const formatLiters = (value) => `${formatNumber(value)} L`;
const formatGallons = (value) => `${formatNumber(value)} galon`;

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("id-ID");
};

const formatDateOnly = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("id-ID", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const formatDailyLabel = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatMonthLabel = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("id-ID", {
    month: "long",
    year: "numeric",
  });
};

const formatWeekLabel = (weekStart, weekEnd) => {
  return `${formatDailyLabel(weekStart)} - ${formatDailyLabel(weekEnd)}`;
};

const getStartOfWeek = (date) => {
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
};

const getEndOfWeek = (date) => {
  const start = getStartOfWeek(date);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return end;
};

const getStartOfMonth = (date) => {
  const d = new Date(date.getFullYear(), date.getMonth(), 1);
  d.setHours(0, 0, 0, 0);
  return d;
};

const getEndOfMonth = (date) => {
  const d = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  d.setHours(23, 59, 59, 999);
  return d;
};

const SummaryCard = ({ icon, title, liters, gallons, subtitle, period }) => (
  <div className="water-usage-summary-card">
    <div className="water-usage-summary-icon">{icon}</div>
    <div className="water-usage-summary-content">
      <span className="water-usage-summary-title">{title}</span>
      <strong className="water-usage-summary-main">{formatLiters(liters)}</strong>
      <span className="water-usage-summary-sub">{formatGallons(gallons)}</span>
      {period ? <small className="water-usage-summary-period">{period}</small> : null}
      {subtitle ? <small className="water-usage-summary-extra">{subtitle}</small> : null}
    </div>
  </div>
);

const SuperAdminWaterUsage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [accounts, setAccounts] = useState([]);
  const [selectedUsername, setSelectedUsername] = useState("");
  const [selectedSensorId, setSelectedSensorId] = useState("all");
  const [chartMode, setChartMode] = useState("daily");

  const [dashboardData, setDashboardData] = useState(null);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [loadingDashboard, setLoadingDashboard] = useState(true);
  const [error, setError] = useState("");

  const authHeaders = useMemo(() => {
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  }, [token]);

  const handleUnauthorized = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("username");
    localStorage.removeItem("is_super_admin");
    navigate("/login", { replace: true });
  }, [navigate]);

  const fetchAccounts = useCallback(async () => {
    if (!token) {
      handleUnauthorized();
      return;
    }

    setLoadingAccounts(true);
    setError("");

    try {
      const response = await axios.get(
        `${API_BASE_URL}/superadmin/tables/accounts?_limit=5000&_offset=0&_sort=username&_order=ASC`,
        { headers: authHeaders }
      );

      const rows = Array.isArray(response.data) ? response.data : [];
      const filteredAccounts = rows
        .filter((item) => Number(item.is_super_admin) !== 1)
        .sort((a, b) => String(a.username).localeCompare(String(b.username)));

      setAccounts(filteredAccounts);

      if (filteredAccounts.length > 0) {
        setSelectedUsername((prev) => prev || filteredAccounts[0].username);
      }
    } catch (err) {
      console.error("Error fetching accounts:", err);
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        handleUnauthorized();
        return;
      }
      setError(err.response?.data?.message || "Gagal mengambil daftar akun.");
      setAccounts([]);
    } finally {
      setLoadingAccounts(false);
    }
  }, [authHeaders, token, handleUnauthorized]);

  const fetchDashboard = useCallback(async () => {
    if (!token) {
      handleUnauthorized();
      return;
    }

    if (!selectedUsername) {
      setDashboardData(null);
      setLoadingDashboard(false);
      return;
    }

    setLoadingDashboard(true);
    setError("");

    try {
      const params = { username: selectedUsername };
      if (selectedSensorId && selectedSensorId !== "all") {
        params.sensor_id = selectedSensorId;
      }

      const response = await axios.get(
        `${API_BASE_URL}/superadmin/tables/water-usage-dashboard`,
        {
          headers: authHeaders,
          params,
        }
      );

      setDashboardData(response.data);
    } catch (err) {
      console.error("Error fetching water usage dashboard:", err);
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        handleUnauthorized();
        return;
      }
      setError(err.response?.data?.message || "Gagal mengambil dashboard penggunaan air.");
      setDashboardData(null);
    } finally {
      setLoadingDashboard(false);
    }
  }, [authHeaders, token, selectedUsername, selectedSensorId, handleUnauthorized]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const accountInfo = dashboardData?.account || null;
  const sensors = Array.isArray(dashboardData?.sensors) ? dashboardData.sensors : [];
  const accountTotals = dashboardData?.account_totals || {};
  const selectedTotals = dashboardData?.selected_totals || {};
  const sensorBreakdown = Array.isArray(dashboardData?.sensor_breakdown)
    ? dashboardData.sensor_breakdown
    : [];
  const trends = dashboardData?.trends || { daily: [], weekly: [], monthly: [] };

  const selectedSensorObject =
    selectedSensorId === "all"
      ? null
      : sensors.find((sensor) => String(sensor.sensor_id) === String(selectedSensorId)) || null;

  const chartRows = useMemo(() => {
    if (chartMode === "daily") return Array.isArray(trends.daily) ? trends.daily : [];
    if (chartMode === "weekly") return Array.isArray(trends.weekly) ? trends.weekly : [];
    return Array.isArray(trends.monthly) ? trends.monthly : [];
  }, [chartMode, trends]);

  const chartLabels = useMemo(() => {
    if (chartMode === "daily") {
      return chartRows.map((item) => formatDailyLabel(item.period_date || item.label));
    }
    if (chartMode === "weekly") {
      return chartRows.map((item) => formatWeekLabel(item.week_start, item.week_end));
    }
    return chartRows.map((item) => formatMonthLabel(item.month_start));
  }, [chartMode, chartRows]);

  const chartData = useMemo(() => {
    return {
      labels: chartLabels,
      datasets: [
        {
          label: "Volume Air (L)",
          data: chartRows.map((item) => Number(item.volume || 0)),
          borderColor: "#0ea5e9",
          backgroundColor: "rgba(14, 165, 233, 0.15)",
          fill: true,
          tension: 0.3,
          pointRadius: 3,
        },
        {
          label: "Total Galon",
          data: chartRows.map((item) => Number(item.gallons || 0)),
          borderColor: "#22c55e",
          backgroundColor: "rgba(34, 197, 94, 0.12)",
          fill: true,
          tension: 0.3,
          pointRadius: 3,
        },
      ],
    };
  }, [chartLabels, chartRows]);

  const now = useMemo(() => new Date(), []);
  const dailyPeriodText = useMemo(() => {
    return `Untuk tanggal ${formatDateOnly(now)}`;
  }, [now]);

  const weeklyPeriodText = useMemo(() => {
    const start = getStartOfWeek(now);
    const end = getEndOfWeek(now);
    return `Periode ${formatDateOnly(start)} - ${formatDateOnly(end)}`;
  }, [now]);

  const monthlyPeriodText = useMemo(() => {
    const start = getStartOfMonth(now);
    const end = getEndOfMonth(now);
    return `Periode ${formatDateOnly(start)} - ${formatDateOnly(end)}`;
  }, [now]);

  const chartPeriodDescription = useMemo(() => {
    if (chartMode === "daily") return "Menampilkan tren 30 hari terakhir.";
    if (chartMode === "weekly") return "Menampilkan tren 12 minggu terakhir.";
    return "Menampilkan tren 12 bulan terakhir.";
  }, [chartMode]);

  const chartTitle = useMemo(() => {
    const scopeText = selectedSensorObject
      ? `Sensor ${selectedSensorObject.nama_sensor}`
      : "Semua sensor air akun";

    if (chartMode === "daily") return `Trend Harian - ${scopeText}`;
    if (chartMode === "weekly") return `Trend Mingguan - ${scopeText}`;
    return `Trend Bulanan - ${scopeText}`;
  }, [chartMode, selectedSensorObject]);

  const chartOptions = useMemo(() => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: "top" },
        title: { display: true, text: chartTitle },
      },
      scales: {
        x: {
          title: {
            display: true,
            text:
              chartMode === "daily"
                ? "Tanggal"
                : chartMode === "weekly"
                ? "Rentang Minggu"
                : "Bulan",
          },
        },
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: "Nilai",
          },
        },
      },
    };
  }, [chartMode, chartTitle]);

  const handleAccountChange = (e) => {
    setSelectedUsername(e.target.value);
    setSelectedSensorId("all");
  };

  const handleSensorChange = (e) => {
    setSelectedSensorId(e.target.value);
  };

  const selectedScopeLabel = selectedSensorObject
    ? `Sensor: ${selectedSensorObject.nama_sensor}`
    : "Semua Sensor Air";

  if (loadingAccounts) {
    return <div className="water-usage-page-state">Memuat daftar akun...</div>;
  }

  if (!loadingAccounts && accounts.length === 0) {
    return <div className="water-usage-page-state">Tidak ada akun pengguna yang tersedia.</div>;
  }

  return (
    <div className="superadmin-water-usage-container">
      <div className="water-usage-header">
        <div>
          <h2>Laporan Penggunaan Air per Akun</h2>
          <p>
            Pantau total pemakaian air harian, mingguan, dan bulanan untuk setiap akun,
            serta rincian per sensor air dengan periode tanggal yang jelas.
          </p>
        </div>

        <button className="water-usage-refresh-btn" onClick={fetchDashboard}>
          <FaSyncAlt /> Refresh
        </button>
      </div>

      {error && <div className="water-usage-error-box">{error}</div>}

      <div className="water-usage-filter-panel">
        <div className="water-usage-filter-item">
          <label>
            <FaFilter /> Pilih Akun
          </label>
          <select value={selectedUsername} onChange={handleAccountChange}>
            {accounts.map((account) => (
              <option key={account.id} value={account.username}>
                {account.depot_name ? `${account.depot_name} (${account.username})` : account.username}
              </option>
            ))}
          </select>
        </div>

        <div className="water-usage-filter-item">
          <label>
            <FaTint /> Pilih Sensor Air
          </label>
          <select value={selectedSensorId} onChange={handleSensorChange}>
            <option value="all">Semua Sensor Air</option>
            {sensors.map((sensor) => (
              <option key={sensor.sensor_id} value={sensor.sensor_id}>
                {sensor.nama_sensor} ({sensor.sensor_id})
              </option>
            ))}
          </select>
        </div>

        <div className="water-usage-filter-item">
          <label>
            <FaDatabase /> Scope Aktif
          </label>
          <div className="water-usage-scope-box">{selectedScopeLabel}</div>
        </div>
      </div>

      {loadingDashboard ? (
        <div className="water-usage-page-state">Memuat dashboard penggunaan air...</div>
      ) : (
        <>
          <div className="water-usage-account-meta">
            <div className="water-usage-account-meta-card">
              <span className="meta-label">Username</span>
              <strong>{accountInfo?.username || "-"}</strong>
            </div>
            <div className="water-usage-account-meta-card">
              <span className="meta-label">Nama Depot / Rumah</span>
              <strong>{accountInfo?.depot_name || "-"}</strong>
            </div>
            <div className="water-usage-account-meta-card">
              <span className="meta-label">Alamat</span>
              <strong>{accountInfo?.address || "-"}</strong>
            </div>
            <div className="water-usage-account-meta-card">
              <span className="meta-label">Jumlah Sensor Air</span>
              <strong>{sensors.length}</strong>
            </div>
          </div>

          <section className="water-usage-section">
            <div className="section-title-row">
              <div>
                <h3>Ringkasan Total Akun</h3>
                <span className="section-subtitle">
                  Akumulasi dari semua sensor air milik akun ini
                </span>
              </div>
            </div>

            <div className="water-usage-summary-grid">
              <SummaryCard
                icon={<FaCalendarDay />}
                title="Total Harian"
                liters={accountTotals.daily_volume}
                gallons={accountTotals.daily_gallons}
                period={dailyPeriodText}
                subtitle={`Update terakhir: ${formatDateTime(accountTotals.latest_timestamp)}`}
              />
              <SummaryCard
                icon={<FaCalendarWeek />}
                title="Total Mingguan"
                liters={accountTotals.weekly_volume}
                gallons={accountTotals.weekly_gallons}
                period={weeklyPeriodText}
                subtitle={`Total record: ${accountTotals.total_records || 0}`}
              />
              <SummaryCard
                icon={<FaCalendarAlt />}
                title="Total Bulanan"
                liters={accountTotals.monthly_volume}
                gallons={accountTotals.monthly_gallons}
                period={monthlyPeriodText}
                subtitle={`Total keseluruhan: ${formatLiters(accountTotals.total_volume || 0)}`}
              />
            </div>
          </section>

          <section className="water-usage-section">
            <div className="section-title-row">
              <div>
                <h3>Ringkasan Scope Terpilih</h3>
                <span className="section-subtitle">
                  {selectedSensorObject
                    ? `Data sensor ${selectedSensorObject.nama_sensor}`
                    : "Menampilkan semua sensor air"}
                </span>
              </div>
            </div>

            <div className="water-usage-summary-grid">
              <SummaryCard
                icon={<FaCalendarDay />}
                title="Harian"
                liters={selectedTotals.daily_volume}
                gallons={selectedTotals.daily_gallons}
                period={dailyPeriodText}
                subtitle={`Update terakhir: ${formatDateTime(selectedTotals.latest_timestamp)}`}
              />
              <SummaryCard
                icon={<FaCalendarWeek />}
                title="Mingguan"
                liters={selectedTotals.weekly_volume}
                gallons={selectedTotals.weekly_gallons}
                period={weeklyPeriodText}
                subtitle={`Total record: ${selectedTotals.total_records || 0}`}
              />
              <SummaryCard
                icon={<FaCalendarAlt />}
                title="Bulanan"
                liters={selectedTotals.monthly_volume}
                gallons={selectedTotals.monthly_gallons}
                period={monthlyPeriodText}
                subtitle={`Total keseluruhan: ${formatLiters(selectedTotals.total_volume || 0)}`}
              />
            </div>
          </section>

          <section className="water-usage-section">
            <div className="section-title-row">
              <div>
                <h3>Grafik Trend Penggunaan Air</h3>
                <span className="section-subtitle">{chartPeriodDescription}</span>
              </div>

              <div className="chart-mode-switcher">
                <button
                  className={chartMode === "daily" ? "active" : ""}
                  onClick={() => setChartMode("daily")}
                >
                  Harian
                </button>
                <button
                  className={chartMode === "weekly" ? "active" : ""}
                  onClick={() => setChartMode("weekly")}
                >
                  Mingguan
                </button>
                <button
                  className={chartMode === "monthly" ? "active" : ""}
                  onClick={() => setChartMode("monthly")}
                >
                  Bulanan
                </button>
              </div>
            </div>

            <div className="water-usage-chart-card">
              {chartRows.length === 0 ? (
                <div className="water-usage-page-state small">
                  Belum ada data trend untuk scope yang dipilih.
                </div>
              ) : (
                <div className="water-usage-chart-wrapper">
                  <Line data={chartData} options={chartOptions} />
                </div>
              )}
            </div>
          </section>

          <section className="water-usage-section">
            <div className="section-title-row">
              <div>
                <h3>Rincian Semua Sensor Air dalam Akun</h3>
                <span className="section-subtitle">
                  Total per sensor air untuk akun {accountInfo?.username || "-"} dengan ringkasan harian, mingguan, dan bulanan aktif
                </span>
              </div>
            </div>

            <div className="water-usage-table-wrapper">
              <table className="water-usage-table">
                <thead>
                  <tr>
                    <th>Sensor ID</th>
                    <th>Nama Sensor</th>
                    <th>Status</th>
                    <th>Harian</th>
                    <th>Mingguan</th>
                    <th>Bulanan</th>
                    <th>Total Record</th>
                    <th>Update Terakhir</th>
                  </tr>
                </thead>
                <tbody>
                  {sensorBreakdown.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="empty-cell">
                        Tidak ada sensor air pada akun ini.
                      </td>
                    </tr>
                  ) : (
                    sensorBreakdown.map((sensor) => (
                      <tr key={sensor.sensor_id}>
                        <td>{sensor.sensor_id}</td>
                        <td>{sensor.nama_sensor || "-"}</td>
                        <td>
                          <span
                            className={`sensor-status-badge ${
                              Number(sensor.is_registered) === 1 ? "registered" : "not-registered"
                            }`}
                          >
                            {Number(sensor.is_registered) === 1 ? "Terdaftar" : "Belum Terdaftar"}
                          </span>
                        </td>
                        <td>
                          <strong>{formatLiters(sensor.daily_volume)}</strong>
                          <small>{dailyPeriodText}</small>
                        </td>
                        <td>
                          <strong>{formatLiters(sensor.weekly_volume)}</strong>
                          <small>{weeklyPeriodText}</small>
                        </td>
                        <td>
                          <strong>{formatLiters(sensor.monthly_volume)}</strong>
                          <small>{monthlyPeriodText}</small>
                        </td>
                        <td>{sensor.total_records || 0}</td>
                        <td>{formatDateTime(sensor.latest_timestamp)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default SuperAdminWaterUsage;